import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, } from 'react';
import { isBrowserEnvironment } from '../internal/utils.js';
import enUS from './en-US.json' with { type: 'json' };
import zhCN from './zh-CN.json' with { type: 'json' };
export const NK_LOCALE_STORAGE_KEY = 'nimeet.ui-kit.locale';
export const NK_STRING_KEYS = Object.keys(enUS);
export const NK_LOCALES = ['zh-CN', 'en-US'];
const BUNDLED = {
    'en-US': enUS,
    'zh-CN': zhCN,
};
function isLocale(value) {
    return value === 'zh-CN' || value === 'en-US';
}
function readStoredLocale(storageKey) {
    if (!isBrowserEnvironment())
        return null;
    try {
        const stored = window.localStorage.getItem(storageKey);
        if (isLocale(stored))
            return stored;
    }
    catch {
        // Private mode / sandboxed iframes: fall back to the browser language.
    }
    return null;
}
/** Stored choice first, then the browser language, then English. */
export function detectNkLocale(storageKey = NK_LOCALE_STORAGE_KEY) {
    const stored = readStoredLocale(storageKey);
    if (stored)
        return stored;
    if (!isBrowserEnvironment())
        return 'en-US';
    const language = typeof navigator !== 'undefined' ? navigator.language : '';
    return language.toLowerCase().startsWith('zh') ? 'zh-CN' : 'en-US';
}
/**
 * Replaces `{name}` placeholders in a resource string.
 * Unknown placeholders are left untouched so a host override cannot silently lose data.
 */
export function formatNkString(template, params) {
    if (!params)
        return template;
    return template.replace(/\{(\w+)\}/g, (match, key) => {
        const value = params[key];
        return value === undefined ? match : String(value);
    });
}
export function resolveNkStrings(locale, overrides) {
    const bundled = BUNDLED[locale] ?? BUNDLED['en-US'];
    if (!overrides)
        return bundled;
    return { ...bundled, ...overrides };
}
const FALLBACK_STRINGS = BUNDLED['en-US'];
const FALLBACK_CONTEXT = {
    locale: 'en-US',
    setLocale: () => undefined,
    toggleLocale: () => undefined,
    strings: FALLBACK_STRINGS,
    t: (key, params) => formatNkString(FALLBACK_STRINGS[key], params),
};
const NkLocaleContext = createContext(null);
/**
 * Locale + string-resource provider. English is the fallback when no provider is mounted, so a
 * single component can be server-rendered in isolation (stories, tests, docs snippets).
 */
export function NkLocaleProvider({ children, locale: controlledLocale, defaultLocale, onLocaleChange, strings, stringsByLocale, storageKey = NK_LOCALE_STORAGE_KEY, persist = true, }) {
    const [internalLocale, setInternalLocale] = useState(() => defaultLocale ?? detectNkLocale(storageKey));
    const locale = controlledLocale ?? internalLocale;
    useEffect(() => {
        if (!isBrowserEnvironment())
            return undefined;
        document.documentElement.lang = locale;
        return () => {
            document.documentElement.lang = '';
        };
    }, [locale]);
    useEffect(() => {
        if (!persist)
            return;
        if (controlledLocale !== undefined)
            return;
        if (!isBrowserEnvironment())
            return;
        try {
            window.localStorage.setItem(storageKey, locale);
        }
        catch {
            // Persisting is best-effort.
        }
    }, [controlledLocale, locale, persist, storageKey]);
    const setLocale = useCallback((next) => {
        if (!isLocale(next))
            return;
        if (controlledLocale === undefined)
            setInternalLocale(next);
        onLocaleChange?.(next);
    }, [controlledLocale, onLocaleChange]);
    const toggleLocale = useCallback(() => {
        setLocale(locale === 'zh-CN' ? 'en-US' : 'zh-CN');
    }, [locale, setLocale]);
    const mergedOverrides = stringsByLocale?.[locale] ?? strings;
    const stringsValue = useMemo(() => resolveNkStrings(locale, mergedOverrides), [locale, mergedOverrides]);
    const context = useMemo(() => ({
        locale,
        setLocale,
        toggleLocale,
        strings: stringsValue,
        t: (key, params) => formatNkString(stringsValue[key], params),
    }), [locale, setLocale, toggleLocale, stringsValue]);
    return _jsx(NkLocaleContext.Provider, { value: context, children: children });
}
/** Access the active locale and its strings; falls back to bundled English without a provider. */
export function useNkStrings() {
    return useContext(NkLocaleContext) ?? FALLBACK_CONTEXT;
}
/** Convenience hook: just the `t()` function. */
export function useNkTranslate() {
    return useNkStrings().t;
}
//# sourceMappingURL=locale.js.map